import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { createOutline, saveOutline } from 'ionicons/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../config';
import './MedicalNotesParkingLot.css';

const ALLOWED_ORIGINS = [
  'https://www.infinity-mc-hms.com',
  'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
  'http://localhost:3000',
  'http://localhost:5000',
  'https://localhost:3000',
  'https://localhost:5000'
];

const ACTION_LABELS = {
  DISMISS: 'Dismiss Suggestion',
  APPOINTMENT: 'Add as an Appointment',
  EXISTING_CONCERN: 'Add to an existing Medical Concern',
  NEW_CONCERN: 'Add to a new Medical Concern'
};

const initialNewConcernModalState = {
  isOpen: false,
  entry: null,
  form: {
    date_identified: new Date().toISOString().split('T')[0],
    subject: '',
    description: ''
  },
  error: null,
  submitting: false,
  dueDate: ''
};

const MedicalNotesParkingLot = () => {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('auth_token') || '');
  const [userId, setUserId] = useState(() => localStorage.getItem('user_id') || '');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [newConcernModal, setNewConcernModal] = useState(initialNewConcernModalState);
  const [dueDateOverrides, setDueDateOverrides] = useState({});
  const [entryOverrides, setEntryOverrides] = useState({});
  const [editingEntries, setEditingEntries] = useState({});
  const [dismissReason, setDismissReason] = useState('');
  const [dismissEntryId, setDismissEntryId] = useState(null);
  const [concernLists, setConcernLists] = useState({});
  const [visibleConcernDrawer, setVisibleConcernDrawer] = useState(null);
  const [concernsLoading, setConcernsLoading] = useState({});
  const [concernsError, setConcernsError] = useState({});
  const [removingEntryIds, setRemovingEntryIds] = useState(new Set());

  const isAuthenticated = useMemo(() => !!authToken && !!userId, [authToken, userId]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event?.data) {
        return;
      }

      const isMetaMask = event.data?.target === 'metamask-contentscript' || event.data?.target === 'metamask-inpage';
      if (isMetaMask || event.data?.type === 'webpackWarnings') {
        return;
      }

      if (!ALLOWED_ORIGINS.includes(event.origin) && event.origin !== window.origin) {
        return;
      }

      if (event.data.type === 'AUTH_DATA') {
        const { auth_token, user_id } = event.data;
        if (auth_token && user_id) {
          setAuthToken(auth_token);
          setUserId(user_id);
          localStorage.setItem('auth_token', auth_token);
          localStorage.setItem('user_id', user_id);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!authToken) return;
    localStorage.setItem('auth_token', authToken);
  }, [authToken]);

  useEffect(() => {
    if (!userId) return;
    localStorage.setItem('user_id', userId);
  }, [userId]);

  const getEffectiveDueDate = useCallback(
    (entryId, fallbackDate) => {
      return dueDateOverrides[entryId] || fallbackDate || '';
    },
    [dueDateOverrides]
  );

  const handleDueDateChange = (entryId, value) => {
    setDueDateOverrides((prev) => {
      if (!value) {
        const { [entryId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [entryId]: value };
    });
  };

  const buildEntryWithOverrides = (entry) => ({
    ...entry,
    ...(entryOverrides[entry.id] || {})
  });

  const handleFieldChange = (entryId, field, value) => {
    setEntryOverrides((prev) => ({
      ...prev,
      [entryId]: {
        ...(prev[entryId] || {}),
        [field]: value
      }
    }));
  };

  const toggleEditing = (entryId) => {
    setEditingEntries((prev) => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };

  const loadConcernsForMember = async (entry) => {
    if (concernLists[entry.members_id]) {
      setVisibleConcernDrawer(entry.id);
      return;
    }

    setConcernsLoading((prev) => ({ ...prev, [entry.id]: true }));
    setConcernsError((prev) => ({ ...prev, [entry.id]: null }));

    try {
      const response = await fetch(`${config.apiUrl}/medicalconcerns/${entry.members_id}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load medical concerns.');
      }

      const concerns = await response.json();
      setConcernLists((prev) => ({
        ...prev,
        [entry.members_id]: Array.isArray(concerns) ? concerns : []
      }));
      setVisibleConcernDrawer(entry.id);
    } catch (err) {
      console.error('Error fetching medical concerns:', err);
      setConcernsError((prev) => ({
        ...prev,
        [entry.id]: err.message || 'Unable to load concerns.'
      }));
    } finally {
      setConcernsLoading((prev) => {
        const { [entry.id]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const ensureDueDate = (entry) => {
    const dueDate = getEffectiveDueDate(entry.id, entry.due_date);
    if (!dueDate) {
      setActionError('Please select a due date before taking action.');
      return null;
    }
    return dueDate;
  };

  const fetchEntries = useCallback(async () => {
    if (!authToken) return;

    setLoading(true);
    setFetchError(null);
    setActionError(null);
    try {
      const response = await fetch(`${config.apiUrl}/medical_note_follow_ups_parking_lot`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load parking lot entries.');
      }

      const result = await response.json();
      setEntries(Array.isArray(result) ? result : []);
      setEntryOverrides({});
      setEditingEntries({});
      setDueDateOverrides({});
      setConcernLists({});
      setVisibleConcernDrawer(null);
    } catch (err) {
      console.error('Error fetching parking lot entries:', err);
      setFetchError(err.message || 'Unable to fetch parking lot entries.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      fetchEntries();
    }
  }, [authToken, fetchEntries]);

  const formatDate = (value) => {
    if (!value) return 'Not provided';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const sendSalesforceTask = async (category, categoryId, followUpDate) => {
    if (!categoryId) {
      throw new Error('Missing category id for Salesforce task.');
    }

    const response = await fetch(`${config.apiUrl}/salesforce_tasks/sent_to_salesforce`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        category,
        category_id: categoryId,
        follow_up_date: followUpDate
      })
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to push Salesforce task.');
    }
  };

  const patchParkingLotEntry = async (entryId, actionLabel, actionDetails = null) => {
    const today = new Date().toISOString().split('T')[0];
    const response = await fetch(`${config.apiUrl}/medical_note_follow_ups_parking_lot/${entryId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        actioned_by_users_id: userId,
        actioned_date: today,
        action_details: actionDetails || actionLabel,
        user_id: userId
      })
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to update parking lot entry.');
    }

    // Animate out before removing
    setRemovingEntryIds((prev) => new Set([...prev, entryId]));
    setTimeout(() => {
      setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      setRemovingEntryIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }, 300);
  };

  const showSuccessToast = (message) => {
    toast.success(message, {
      position: 'top-right',
      autoClose: 3000
    });
  };

  const submitDismissReason = async () => {
    if (!dismissEntryId) return;
    if (!dismissReason.trim()) {
      setActionError('Please provide a reason before dismissing.');
      return;
    }

    setActionError(null);
    withActionLoader(dismissEntryId, async () => {
      const details = `Task dismissed (reason given: ${dismissReason.trim()})`;
      await patchParkingLotEntry(dismissEntryId, ACTION_LABELS.DISMISS, details);
      showSuccessToast('Suggestion dismissed.');
      setDismissEntryId(null);
      setDismissReason('');
    }).catch((err) => {
      console.error('Dismiss action failed:', err);
      setActionError(err.message || 'Failed to dismiss suggestion.');
    });
  };

  const withActionLoader = async (entryId, fn) => {
    setActionLoading((prev) => ({ ...prev, [entryId]: true }));
    try {
      await fn();
    } finally {
      setActionLoading((prev) => {
        const { [entryId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDismiss = (entry) => {
    if (!isAuthenticated) return;
    setDismissEntryId(entry.id);
    setDismissReason('');
  };

  const handleAddAppointment = (entry) => {
    if (!isAuthenticated) return;
    const dueDate = ensureDueDate(entry);
    if (!dueDate) return;
    setActionError(null);

    const mergedEntry = buildEntryWithOverrides(entry);

    withActionLoader(entry.id, async () => {
      const consultationResponse = await fetch(`${config.apiUrl}/consultations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          members_id: entry.members_id,
          service_provider: mergedEntry.service_provider,
          due_next: dueDate,
          concern: mergedEntry.reason,
          specialty: mergedEntry.specialty,
          users_id: userId,
          internal_note: 'Added through Medical Notes Parking Lot'
        })
      });

      if (!consultationResponse.ok) {
        const text = await consultationResponse.text();
        throw new Error(text || 'Failed to create consultation.');
      }

      const consultation = await consultationResponse.json();
      await sendSalesforceTask('Consultation', consultation.id, dueDate);
      await patchParkingLotEntry(entry.id, ACTION_LABELS.APPOINTMENT);
      showSuccessToast('Appointment added and noted.');
    }).catch((err) => {
      console.error('Add appointment failed:', err);
      setActionError(err.message || 'Failed to add appointment.');
    });
  };

  const handleQuickLink = async (entry, concernId) => {
    const dueDate = ensureDueDate(entry);
    if (!dueDate) {
      return;
    }

    const mergedEntry = buildEntryWithOverrides(entry);
    setVisibleConcernDrawer(null);

    setActionError(null);
    await withActionLoader(entry.id, async () => {
      const taskResponse = await fetch(`${config.apiUrl}/medicalconcerntasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          medicalconcerns_id: concernId,
          service_provider: mergedEntry.service_provider,
          due_date: dueDate,
          task: mergedEntry.reason,
          person_responsible: 'concierge',
          status: 'Ongoing'
        })
      });

      if (!taskResponse.ok) {
        const text = await taskResponse.text();
        throw new Error(text || 'Failed to create medical concern task.');
      }

      const task = await taskResponse.json();
      await sendSalesforceTask('Medical Concern Task', task.id, dueDate);
      await patchParkingLotEntry(entry.id, ACTION_LABELS.EXISTING_CONCERN);
      showSuccessToast('Task linked to the selected concern.');
    }).catch((err) => {
      console.error('Quick link failed:', err);
      setActionError(err.message || 'Failed to link task.');
    });
  };

  const openNewConcernModal = (entry) => {
    if (!isAuthenticated) return;
    const dueDate = ensureDueDate(entry);
    if (!dueDate) return;
    const mergedEntry = buildEntryWithOverrides(entry);
    setNewConcernModal({
      ...initialNewConcernModalState,
      isOpen: true,
      entry: mergedEntry,
      dueDate,
      form: {
        date_identified: new Date().toISOString().split('T')[0],
        subject: '',
        description: ''
      }
    });
  };

  const closeNewConcernModal = () => {
    setNewConcernModal(initialNewConcernModalState);
  };

  const handleNewConcernChange = (field, value) => {
    setNewConcernModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value
      }
    }));
  };

  const handleNewConcernSubmit = async () => {
    if (!newConcernModal.entry) return;
    const dueDate = newConcernModal.dueDate;
    if (!dueDate) {
      setNewConcernModal((prev) => ({
        ...prev,
        error: 'Due date is required.'
      }));
      return;
    }
    const { subject, description, date_identified } = newConcernModal.form;

    if (!subject.trim() || !description.trim() || !date_identified) {
      setNewConcernModal((prev) => ({
        ...prev,
        error: 'All fields are required.'
      }));
      return;
    }

    setNewConcernModal((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const concernResponse = await fetch(`${config.apiUrl}/medicalconcerns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          members_id: newConcernModal.entry.members_id,
          date_identified,
          subject,
          description
        })
      });

      if (!concernResponse.ok) {
        const text = await concernResponse.text();
        throw new Error(text || 'Failed to create medical concern.');
      }

      const concern = await concernResponse.json();
      const taskResponse = await fetch(`${config.apiUrl}/medicalconcerntasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          medicalconcerns_id: concern.id,
          service_provider: newConcernModal.entry.service_provider,
          due_date: dueDate,
          person_responsible: 'concierge',
          task: newConcernModal.entry.reason,
          status: 'Ongoing'
        })
      });

      if (!taskResponse.ok) {
        const text = await taskResponse.text();
        throw new Error(text || 'Failed to create medical concern task.');
      }

      const task = await taskResponse.json();
      await sendSalesforceTask('Medical Concern Task', task.id, dueDate);
      await patchParkingLotEntry(newConcernModal.entry.id, ACTION_LABELS.NEW_CONCERN);
      closeNewConcernModal();
      showSuccessToast('New medical concern created and task added.');
    } catch (err) {
      console.error('Error creating new medical concern:', err);
      setNewConcernModal((prev) => ({
        ...prev,
        submitting: false,
        error: err.message || 'Failed to create medical concern.'
      }));
    }
  };

  const renderEntryCard = (entry) => {
    const disabled = actionLoading[entry.id];
    const dueDateOverride = dueDateOverrides[entry.id] || '';
    const dueDate = getEffectiveDueDate(entry.id, entry.due_date);
    const needsDueDate = !dueDate;
    const showDueDateInput = !entry.due_date;
    const isEditing = editingEntries[entry.id];
    const isRemoving = removingEntryIds.has(entry.id);
    const mergedEntry = buildEntryWithOverrides(entry);
    const serviceProvider = mergedEntry.service_provider || 'Unknown Provider';
    const specialty = mergedEntry.specialty;
    const reasonText = mergedEntry.reason || 'Follow-up suggestion';

    return (
      <div key={entry.id} className={`parking-card ${isRemoving ? 'removing' : ''}`}>
        <div className="parking-card-header">
          <div className="member-name-row">
            <span className="member-name">
              {entry.member_firstname || entry.member_surname
                ? `${entry.member_firstname || ''} ${entry.member_surname || ''}`.trim()
                : 'Member'}
            </span>
            <div className="card-actions-inline">
              <button
                className="icon-button"
                onClick={() => toggleEditing(entry.id)}
                title={isEditing ? 'Save edits' : 'Edit details'}
              >
                <IonIcon icon={isEditing ? saveOutline : createOutline} />
              </button>
            </div>
          </div>
          {isEditing ? (
            <label className="edit-field">
              <span>Reason / Summary</span>
              <textarea
                value={reasonText}
                onChange={(e) => handleFieldChange(entry.id, 'reason', e.target.value)}
                rows={2}
              />
            </label>
          ) : (
            <p className="follow-up-reason">
              {reasonText}
            </p>
          )}
        </div>
        <div className={`parking-card-due ${needsDueDate ? 'missing' : ''}`}>
          <span className="label">Due Date</span>
          {showDueDateInput ? (
            dueDate ? (
              <div className="due-date-confirmed">
                <span className="value">{formatDate(dueDate)}</span>
                <button
                  type="button"
                  className="link-button small"
                  onClick={() => handleDueDateChange(entry.id, '')}
                >
                  Change date
                </button>
              </div>
            ) : (
              <>
                <input
                  type="date"
                  className="due-date-input"
                  value={dueDateOverride}
                  onChange={(e) => handleDueDateChange(entry.id, e.target.value)}
                />
                <span className="due-date-hint">Select a date before taking action.</span>
              </>
            )
          ) : (
            <span className="value">{formatDate(dueDate)}</span>
          )}
        </div>
        <div className="parking-card-provider-block">
          <span className="label">Healthcare Provider</span>
          {isEditing ? (
            <>
              <input
                className="edit-input"
                value={serviceProvider}
                onChange={(e) => handleFieldChange(entry.id, 'service_provider', e.target.value)}
                placeholder="Provider name"
              />
              <input
                className="edit-input"
                value={specialty || ''}
                onChange={(e) => handleFieldChange(entry.id, 'specialty', e.target.value)}
                placeholder="Specialty"
              />
            </>
          ) : (
            <span className="value">
              {serviceProvider}
              {specialty ? ` (${specialty})` : ''}
            </span>
          )}
        </div>
        <div className="parking-card-actions">
          <button
            className="parking-btn secondary"
            onClick={() => handleDismiss(entry)}
            disabled={disabled}
          >
            {actionLoading[entry.id] ? 'Processing...' : ACTION_LABELS.DISMISS}
          </button>
          <button
            className="parking-btn primary"
            onClick={() => handleAddAppointment(entry)}
            disabled={disabled || needsDueDate}
          >
            {ACTION_LABELS.APPOINTMENT}
          </button>
          <button
            className="parking-btn primary-outline"
            onClick={() => loadConcernsForMember(entry)}
            disabled={disabled}
          >
            {ACTION_LABELS.EXISTING_CONCERN}
          </button>
          <button
            className="parking-btn primary-outline"
            onClick={() => openNewConcernModal(entry)}
            disabled={disabled || needsDueDate}
          >
            {ACTION_LABELS.NEW_CONCERN}
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="auth-warning">
          <h2>Awaiting Authentication</h2>
          <p>Please ensure auth credentials are provided by the parent application.</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="loading">
          <div className="spinner" />
          <p>Loading parking lot entries...</p>
        </div>
      );
    }

    if (fetchError) {
      return (
        <div className="error">
          <p>{fetchError}</p>
          <button className="retry-button" onClick={fetchEntries}>Retry</button>
        </div>
      );
    }

    if (!entries.length) {
      return (
        <div className="no-data">
          <p>No pending medical note follow-ups found.</p>
        </div>
      );
    }

    return (
      <div className="parking-grid">
        {entries.map(renderEntryCard)}
      </div>
    );
  };

  return (
    <div className="medical-notes-container"> 

      {actionError && (
        <div className="action-error">
          <p>{actionError}</p>
          <button className="link-button" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {renderContent()}

      {newConcernModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Medical Concern</h3>
              <button className="close-button" onClick={closeNewConcernModal} disabled={newConcernModal.submitting}>×</button>
            </div>
            <div className="modal-body">
              <label className="modal-field">
                <span>Date Identified</span>
                <input
                  type="date"
                  value={newConcernModal.form.date_identified}
                  onChange={(e) => handleNewConcernChange('date_identified', e.target.value)}
                  disabled={newConcernModal.submitting}
                />
              </label>
              <label className="modal-field">
                <span>Subject</span>
                <input
                  type="text"
                  value={newConcernModal.form.subject}
                  onChange={(e) => handleNewConcernChange('subject', e.target.value)}
                  placeholder="Brief summary"
                  disabled={newConcernModal.submitting}
                />
              </label>
              <label className="modal-field">
                <span>Description</span>
                <textarea
                  rows="4"
                  value={newConcernModal.form.description}
                  onChange={(e) => handleNewConcernChange('description', e.target.value)}
                  placeholder="Add helpful context"
                  disabled={newConcernModal.submitting}
                />
              </label>
              {newConcernModal.error && <p className="modal-error">{newConcernModal.error}</p>}
            </div>
            <div className="modal-footer">
              <button className="parking-btn secondary" onClick={closeNewConcernModal} disabled={newConcernModal.submitting}>
                Cancel
              </button>
              <button className="parking-btn primary" onClick={handleNewConcernSubmit} disabled={newConcernModal.submitting}>
                {newConcernModal.submitting ? 'Saving...' : 'Create & Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {visibleConcernDrawer && (
        <div className="concern-drawer-overlay" onClick={() => setVisibleConcernDrawer(null)}>
          <div className="concern-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="concern-drawer-header">
              <h4>Existing Medical Concerns</h4>
              <button className="close-button" onClick={() => setVisibleConcernDrawer(null)}>×</button>
            </div>
            <div className="concern-drawer-body">
              {concernsLoading[visibleConcernDrawer] && (
                <div className="loading">
                  <div className="spinner" />
                  <p>Loading concerns...</p>
                </div>
              )}
              {concernsError[visibleConcernDrawer] && (
                <p className="modal-error">{concernsError[visibleConcernDrawer]}</p>
              )}
              {!concernsLoading[visibleConcernDrawer] && !concernsError[visibleConcernDrawer] && (() => {
                const entry = entries.find((item) => item.id === visibleConcernDrawer);
                const list = entry ? (concernLists[entry.members_id] || []) : [];

                if (!list.length) {
                  return <p>No concerns found for this member.</p>;
                }

                return (
                  <ul className="concern-list">
                    {list.map((concern) => (
                      <li key={concern.id}>
                        <div className="concern-list-text">
                          <strong>{concern.subject}</strong>
                          <small>Identified {formatDate(concern.date_identified)}</small>
                        </div>
                        <button
                          className="parking-btn primary"
                          onClick={() => handleQuickLink(entry, concern.id)}
                        >
                          Link
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {dismissEntryId && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Reason for Dismissal</h3>
              <button
                className="close-button"
                onClick={() => {
                  setDismissEntryId(null);
                  setDismissReason('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <label className="modal-field">
                <span>Please provide a brief reason</span>
                <textarea
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  rows={3}
                  placeholder="e.g., duplicate entry, already actioned externally, etc."
                />
              </label>
            </div>
            <div className="modal-footer">
              <button
                className="parking-btn secondary"
                onClick={() => {
                  setDismissEntryId(null);
                  setDismissReason('');
                }}
              >
                Cancel
              </button>
              <button
                className="parking-btn primary"
                onClick={submitDismissReason}
                disabled={!dismissReason.trim()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default MedicalNotesParkingLot;

